import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

const PIN_POSITIONS = [95, 113, 131, 154, 172, 190];
const C_PRIMARY = '#6366f1';
const C_DIM     = '#3730a3';
const C_BODY    = '#0d1117';
const C_SURFACE = '#161b22';

const chipVariants = {
  initial: {
    x: -1800,
    y: -600,
    rotateX: 30,
    rotateZ: 20,
    opacity: 0,
  },
  animate: {
    x: 0,
    y: 0,
    rotateX: 10,
    rotateZ: 0,
    rotateY: -5,
    opacity: 0.82,
    transition: {
      delay: 0.2,
      duration: 1,
      type: 'spring',
      stiffness: 20,
      opacity: { duration: 0.6, delay: 0.2 },
      rotateZ: { duration: 2 },
      rotateX: { duration: 2 },
      rotateY: { duration: 2 },
    },
  },
  float: {
    y: [0, -18, 0],
    x: [0, -10, 0],
    rotateX: [10, -22, 10],
    rotateZ: [0, 0],
    rotateY: [-5, -5],
    opacity: 0.82,
    transition: {
      repeat: Infinity,
      duration: 3,
      ease: 'easeInOut',
    },
  },
};

const ChipGraphic = ({ scrollY = 0 }) => {
  const controls = useAnimation();
  const translateY = scrollY * 0.4;
  const translateX = -scrollY * 0.1;

  useEffect(() => {
    const sequence = async () => {
      await controls.start('animate');
      controls.start('float');
    };
    sequence();
  }, [controls]);

  return (
    /* Outer div — scroll-driven parallax only */
    <div
      className="absolute right-6 top-28 hidden lg:block pointer-events-none select-none"
      style={{ transform: `translateY(${translateY}px) translateX(${translateX}px)` }}
    >
      {/* Inner motion.div — entrance spring → idle float */}
      <motion.div
        variants={chipVariants}
        initial="initial"
        animate={controls}
        style={{ perspective: 800 }}
      >
        <svg
          width="250"
          height="250"
          viewBox="0 0 300 300"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'drop-shadow(0 0 12px rgba(99,102,241,0.22))' }}
        >
          {/* Left pins */}
          {PIN_POSITIONS.map((y, i) => (
            <g key={`lp${i}`}>
              <line x1="70" y1={y} x2="36" y2={y} stroke={i < 3 ? C_PRIMARY : C_DIM} strokeWidth="1.5" />
              <rect x="26" y={y - 4} width="10" height="8" rx="1.5"
                fill={C_SURFACE} stroke={i < 3 ? C_PRIMARY : C_DIM} strokeWidth="0.8" />
            </g>
          ))}

          {/* Right pins */}
          {PIN_POSITIONS.map((y, i) => (
            <g key={`rp${i}`}>
              <line x1="230" y1={y} x2="264" y2={y} stroke={i >= 3 ? C_PRIMARY : C_DIM} strokeWidth="1.5" />
              <rect x="264" y={y - 4} width="10" height="8" rx="1.5"
                fill={C_SURFACE} stroke={i >= 3 ? C_PRIMARY : C_DIM} strokeWidth="0.8" />
            </g>
          ))}

          {/* Top pins */}
          {PIN_POSITIONS.map((x, i) => (
            <g key={`tp${i}`}>
              <line x1={x} y1="70" x2={x} y2="36" stroke={i % 2 === 0 ? C_PRIMARY : C_DIM} strokeWidth="1.5" />
              <rect x={x - 4} y="26" width="8" height="10" rx="1.5"
                fill={C_SURFACE} stroke={i % 2 === 0 ? C_PRIMARY : C_DIM} strokeWidth="0.8" />
            </g>
          ))}

          {/* Bottom pins */}
          {PIN_POSITIONS.map((x, i) => (
            <g key={`bp${i}`}>
              <line x1={x} y1="230" x2={x} y2="264" stroke={i % 2 !== 0 ? C_PRIMARY : C_DIM} strokeWidth="1.5" />
              <rect x={x - 4} y="264" width="8" height="10" rx="1.5"
                fill={C_SURFACE} stroke={i % 2 !== 0 ? C_PRIMARY : C_DIM} strokeWidth="0.8" />
            </g>
          ))}

          {/* Chip body — pulses */}
          <motion.rect
            x="70" y="70" width="160" height="160" rx="6"
            fill={C_BODY} stroke={C_PRIMARY} strokeWidth="1.5"
            animate={{ opacity: [0.75, 1, 0.75] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Pin 1 marker */}
          <motion.circle cx="79" cy="79" r="3.5" fill={C_PRIMARY}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Dot grid */}
          {Array.from({ length: 7 }, (_, row) =>
            Array.from({ length: 7 }, (_, col) => (
              <circle key={`d${row}${col}`}
                cx={86 + col * 21} cy={86 + row * 21}
                r="1" fill="rgba(99,102,241,0.18)" />
            ))
          )}

          {/* ARM Core */}
          <rect x="80" y="80" width="72" height="68" rx="3"
            fill="rgba(99,102,241,0.14)" stroke="rgba(99,102,241,0.4)" strokeWidth="0.75" />
          <text x="116" y="107" textAnchor="middle" fill="#a5b4fc"
            fontSize="8" fontFamily="'JetBrains Mono', monospace" fontWeight="700">ARM</text>
          <text x="116" y="119" textAnchor="middle" fill="#a5b4fc"
            fontSize="7" fontFamily="'JetBrains Mono', monospace">Cortex-A55</text>
          <text x="116" y="138" textAnchor="middle" fill="rgba(165,180,252,0.45)"
            fontSize="6" fontFamily="monospace">32-bit / 64-bit</text>

          {/* L2 Cache */}
          <rect x="157" y="80" width="63" height="28" rx="3"
            fill="rgba(59,130,246,0.1)" stroke="rgba(59,130,246,0.3)" strokeWidth="0.75" />
          <text x="188" y="98" textAnchor="middle" fill="#93c5fd"
            fontSize="7.5" fontFamily="'JetBrains Mono', monospace">L2 Cache</text>

          {/* Crypto Engine */}
          <rect x="157" y="113" width="63" height="35" rx="3"
            fill="rgba(34,197,94,0.08)" stroke="rgba(34,197,94,0.28)" strokeWidth="0.75" />
          <text x="188" y="128" textAnchor="middle" fill="#86efac"
            fontSize="7.5" fontFamily="'JetBrains Mono', monospace">AES-256</text>
          <text x="188" y="140" textAnchor="middle" fill="#86efac"
            fontSize="7" fontFamily="monospace">RSA-2048</text>

          {/* OTA Engine */}
          <rect x="157" y="153" width="63" height="27" rx="3"
            fill="rgba(168,85,247,0.08)" stroke="rgba(168,85,247,0.28)" strokeWidth="0.75" />
          <text x="188" y="165" textAnchor="middle" fill="#c4b5fd"
            fontSize="7.5" fontFamily="'JetBrains Mono', monospace">OTA Engine</text>
          <text x="188" y="175" textAnchor="middle" fill="rgba(196,181,253,0.45)"
            fontSize="6" fontFamily="monospace">SHA-256 verify</text>

          {/* I/O Controller */}
          <rect x="80" y="153" width="72" height="27" rx="3"
            fill="rgba(251,146,60,0.08)" stroke="rgba(251,146,60,0.28)" strokeWidth="0.75" />
          <text x="116" y="165" textAnchor="middle" fill="#fdba74"
            fontSize="7.5" fontFamily="'JetBrains Mono', monospace">I2C / UART</text>
          <text x="116" y="175" textAnchor="middle" fill="rgba(253,186,116,0.45)"
            fontSize="6" fontFamily="monospace">USB / GPIO</text>

          {/* System Bus */}
          <rect x="80" y="185" width="140" height="15" rx="2.5"
            fill="rgba(99,102,241,0.07)" stroke="rgba(99,102,241,0.22)" strokeWidth="0.75" />
          <text x="150" y="196" textAnchor="middle" fill="rgba(165,180,252,0.55)"
            fontSize="6" fontFamily="'JetBrains Mono', monospace" letterSpacing="2">SYSTEM BUS</text>

          {/* Internal trace lines */}
          <line x1="70" y1="95" x2="80" y2="95" stroke="rgba(99,102,241,0.25)" strokeWidth="0.75" />
          <line x1="70" y1="113" x2="80" y2="113" stroke="rgba(99,102,241,0.25)" strokeWidth="0.75" />
          <line x1="230" y1="95" x2="220" y2="95" stroke="rgba(59,130,246,0.25)" strokeWidth="0.75" />
          <line x1="230" y1="131" x2="220" y2="131" stroke="rgba(34,197,94,0.25)" strokeWidth="0.75" />
          <line x1="230" y1="172" x2="220" y2="172" stroke="rgba(168,85,247,0.25)" strokeWidth="0.75" />
          <line x1="131" y1="70" x2="131" y2="80" stroke="rgba(99,102,241,0.25)" strokeWidth="0.75" />
          <line x1="154" y1="230" x2="154" y2="220" stroke="rgba(251,146,60,0.25)" strokeWidth="0.75" />
        </svg>
      </motion.div>
    </div>
  );
};

export default ChipGraphic;
